import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";

import {
  subirDocumentoCliente,
  eliminarDocumentoCliente,
} from "../helpers/rutaClientes";

const clean = (v) => String(v ?? "").trim();

const nombreCliente = (cliente = {}) => {
  const armado = [cliente.apellido1, cliente.apellido2, cliente.nombre]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(cliente.razonsocial) || "Cliente";
};

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return "-";

  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;

  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (v) => {
  if (!v) return "-";

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleDateString("es-AR");
};

const isImage = (doc = {}) =>
  String(doc.mimeType || "").startsWith("image/") ||
  /\.(jpg|jpeg|png|webp)$/i.test(String(doc.url || ""));

const getClienteFromResp = (resp, fallback) => {
  if (resp?.cliente) return resp.cliente;
  if (resp?.data?.cliente) return resp.data.cliente;
  if (resp?.data) return resp.data;
  return fallback;
};

const DocumentoCard = ({
  titulo,
  tipo,
  ayuda,
  accept,
  doc,
  uploading,
  deleting,
  onUpload,
  onDelete,
}) => {
  const tieneArchivo = !!doc?.url;

  return (
    <div
      className="border rounded p-3 mb-3"
      style={{ background: "#fafafa" }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{titulo}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {ayuda}
          </div>
        </div>

        <span
          className={`badge ${tieneArchivo ? "bg-success" : "bg-secondary"}`}
        >
          {tieneArchivo ? "Cargado" : "Pendiente"}
        </span>
      </div>

      {tieneArchivo ? (
        <div className="mb-2">
          {isImage(doc) ? (
            <a href={doc.url} target="_blank" rel="noreferrer">
              <img
                src={doc.url}
                alt={titulo}
                style={{
                  width: "100%",
                  maxHeight: 180,
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
            </a>
          ) : (
            <div
              className="border rounded p-3 text-center"
              style={{ background: "#fff" }}
            >
              <i
                className="fa fa-file-pdf-o"
                aria-hidden="true"
                style={{ fontSize: 38, color: "#b71c1c" }}
              />
              <div className="mt-2">
                <a href={doc.url} target="_blank" rel="noreferrer">
                  Ver PDF
                </a>
              </div>
            </div>
          )}

          <div className="mt-2" style={{ fontSize: 12 }}>
            <div>
              <strong>Archivo:</strong> {doc.nombreOriginal || "-"}
            </div>
            <div>
              <strong>Tamaño:</strong> {fmtSize(doc.size)}
            </div>
            <div>
              <strong>Fecha:</strong> {fmtDate(doc.fechaSubida)}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted mb-2" style={{ fontSize: 13 }}>
          Todavía no se cargó archivo.
        </div>
      )}

      <div className="d-flex gap-2 flex-wrap">
        <label className="btn btn-primary btn-sm mb-0">
          {uploading ? "Subiendo..." : tieneArchivo ? "Reemplazar" : "Subir"}
          <input
            type="file"
            accept={accept}
            hidden
            disabled={uploading || deleting}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";

              if (file) {
                onUpload(tipo, file);
              }
            }}
          />
        </label>

        {tieneArchivo && (
          <>
            <a
              className="btn btn-outline-secondary btn-sm"
              href={doc.url}
              target="_blank"
              rel="noreferrer"
            >
              Ver
            </a>

            <Button
              variant="outline-danger"
              size="sm"
              disabled={uploading || deleting}
              onClick={() => onDelete(tipo)}
            >
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default function ModalDocumentosCliente({
  show,
  cliente,
  onHide,
  onClienteActualizado,
}) {
  const [localCliente, setLocalCliente] = useState(cliente || null);
  const [loadingTipo, setLoadingTipo] = useState("");
  const [deletingTipo, setDeletingTipo] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setLocalCliente(cliente || null);
      setError("");
      setLoadingTipo("");
      setDeletingTipo("");
    }
  }, [show, cliente]);

  const documentos = useMemo(
    () => localCliente?.documentos || {},
    [localCliente]
  );

  const handleUpload = async (tipo, file) => {
    try {
      setError("");

      if (!localCliente?._id) {
        setError("No se encontró el ID del cliente.");
        return;
      }

      const permitidos = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];

      if (!permitidos.includes(file.type)) {
        setError("Solo se permiten archivos JPG, PNG o PDF.");
        return;
      }

      const max = 3 * 1024 * 1024;

      if (file.size > max) {
        setError("El archivo supera los 3 MB permitidos.");
        return;
      }

      setLoadingTipo(tipo);

      const resp = await subirDocumentoCliente(localCliente._id, tipo, file);
      const actualizado = getClienteFromResp(resp, localCliente);

      setLocalCliente(actualizado);

      if (onClienteActualizado) {
        onClienteActualizado(actualizado);
      }
    } catch (e) {
      console.error("❌ Error subiendo documento:", e);
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo subir el documento."
      );
    } finally {
      setLoadingTipo("");
    }
  };

  const handleDelete = async (tipo) => {
    if (!localCliente?._id) {
      setError("No se encontró el ID del cliente.");
      return;
    }

    if (!window.confirm("¿Eliminar este documento?")) return;

    try {
      setError("");
      setDeletingTipo(tipo);

      const resp = await eliminarDocumentoCliente(localCliente._id, tipo);
      const actualizado = getClienteFromResp(resp, localCliente);

      setLocalCliente(actualizado);

      if (onClienteActualizado) {
        onClienteActualizado(actualizado);
      }
    } catch (e) {
      console.error("❌ Error eliminando documento:", e);
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "No se pudo eliminar el documento."
      );
    } finally {
      setDeletingTipo("");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Documentos del Cliente</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!localCliente ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Cargando cliente...</div>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div style={{ fontWeight: 700, fontSize: 17 }}>
                {nombreCliente(localCliente)}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Código: {localCliente.codcli || "-"} | ID / Documento:{" "}
                {localCliente.dni || "-"}
              </div>
            </div>

            {error && (
              <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                {error}
              </div>
            )}

            <DocumentoCard
              titulo="Frente del documento"
              tipo="frente"
              ayuda="Imagen del frente. Formatos: JPG o PNG."
              accept="image/jpeg,image/jpg,image/png"
              doc={documentos.frente}
              uploading={loadingTipo === "frente"}
              deleting={deletingTipo === "frente"}
              onUpload={handleUpload}
              onDelete={handleDelete}
            />

            <DocumentoCard
              titulo="Dorso del documento"
              tipo="dorso"
              ayuda="Imagen del dorso. Formatos: JPG o PNG."
              accept="image/jpeg,image/jpg,image/png"
              doc={documentos.dorso}
              uploading={loadingTipo === "dorso"}
              deleting={deletingTipo === "dorso"}
              onUpload={handleUpload}
              onDelete={handleDelete}
            />

            <DocumentoCard
              titulo="PDF adicional"
              tipo="pdf"
              ayuda="Contrato, autorización o archivo adicional. Formato: PDF."
              accept="application/pdf"
              doc={documentos.pdf}
              uploading={loadingTipo === "pdf"}
              deleting={deletingTipo === "pdf"}
              onUpload={handleUpload}
              onDelete={handleDelete}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}